import { Directive, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';

/**
 * Permite soltar archivos arrastrados sobre el elemento.
 * Mientras se arrastra un archivo encima, agrega la clase "file-drop--activo".
 *
 * Uso: <div appFileDrop fileDropAccept="image/png, image/jpeg"
 *           (fileDropped)="subir($event)" (fileRejected)="avisar()"></div>
 */
@Directive({
    selector: '[appFileDrop]',
    standalone: true
})
export class FileDropDirective {

    /** Tipos permitidos, con el mismo formato que el atributo accept del input (p. ej. "image/png, image/jpeg") */
    @Input() fileDropAccept = '';

    /** Desactiva el arrastre, p. ej. mientras se procesa una imagen */
    @Input() fileDropDisabled = false;

    /** Archivos soltados que cumplen con fileDropAccept */
    @Output() fileDropped = new EventEmitter<File[]>();

    /** Se soltaron archivos, pero ninguno es de un tipo permitido */
    @Output() fileRejected = new EventEmitter<File[]>();

    @HostBinding('class.file-drop--activo') active = false;

    // dragenter/dragleave también se disparan al pasar sobre los hijos del elemento:
    // el contador evita que el resaltado parpadee
    private dragDepth = 0;

    @HostListener('dragenter', ['$event'])
    onDragEnter(event: DragEvent): void {
        if (!this.hasFiles(event)) return;
        event.preventDefault();
        this.dragDepth++;
        this.active = !this.fileDropDisabled;
    }

    @HostListener('dragover', ['$event'])
    onDragOver(event: DragEvent): void {
        if (!this.hasFiles(event)) return;
        // Sin preventDefault el navegador no permite soltar y abre el archivo en la pestaña
        event.preventDefault();
        event.dataTransfer.dropEffect = this.fileDropDisabled ? 'none' : 'copy';
    }

    @HostListener('dragleave', ['$event'])
    onDragLeave(event: DragEvent): void {
        if (!this.hasFiles(event)) return;
        this.dragDepth = Math.max(0, this.dragDepth - 1);
        if (this.dragDepth === 0) this.active = false;
    }

    @HostListener('drop', ['$event'])
    onDrop(event: DragEvent): void {
        if (!this.hasFiles(event)) return;
        event.preventDefault();
        this.dragDepth = 0;
        this.active = false;
        if (this.fileDropDisabled) return;

        const files = Array.from(event.dataTransfer.files);
        const accepted = files.filter(file => this.isAccepted(file));
        if (accepted.length > 0) {
            this.fileDropped.emit(accepted);
        } else if (files.length > 0) {
            this.fileRejected.emit(files);
        }
    }

    /** Solo reacciona a archivos, no a texto o enlaces arrastrados */
    private hasFiles(event: DragEvent): boolean {
        return Array.from(event.dataTransfer?.types ?? []).includes('Files');
    }

    private isAccepted(file: File): boolean {
        const accepted = this.fileDropAccept.split(',').map(type => type.trim().toLowerCase()).filter(Boolean);
        if (accepted.length === 0) return true;
        const name = file.name.toLowerCase();
        const type = file.type.toLowerCase();
        return accepted.some(rule =>
            rule.startsWith('.') ? name.endsWith(rule)            // ".png"
            : rule.endsWith('/*') ? type.startsWith(rule.slice(0, -1)) // "image/*"
            : type === rule                                       // "image/png"
        );
    }
}
